import noop from 'lodash/noop';
import { v4 } from 'uuid';

import {
  LogLevel,
  LogMetadata,
  Loggable,
  isDebugEnabled,
} from './loggers/base';
import { CliConsoleLogger } from './loggers/cli-console';
import { ConsoleLogger } from './loggers/console';
import { DatadogBrowserLogger } from './loggers/datadog-browser-logger';
import { DriverBasedLogger } from './loggers/driver-based-logger';
import { LocalLokiLogger } from './loggers/local-loki-logger';

export type { LogLevel, LogMetadata, Loggable };

export {
  DriverBasedLogger,
  ConsoleLogger,
  DatadogBrowserLogger,
  CliConsoleLogger,
  LocalLokiLogger,
  isDebugEnabled,
};

type Options = {
  logger?: Loggable;
  metadata?: LogMetadata;
  consoleLogWarningsAsInfo?: boolean;
};

export class Logger {
  public logger: Loggable;
  private internalMetadata: LogMetadata;
  private consoleLogWarningsAsInfo: boolean;

  /**
   * @deprecated Don't instantiate this class directly unless you have a specific use case.
   * Prefer using `useNamedLogger` or `useExtendedLogger` hooks which will provide you with a logger instance
   * and will automatically include important metadata in the logs eg `tenantId`, `userId`, `appointmentId` etc.
   */
  constructor(prefix = 'eluve-logger', options: Options = {}) {
    const { logger, metadata, consoleLogWarningsAsInfo } = options;
    this.consoleLogWarningsAsInfo = Boolean(consoleLogWarningsAsInfo);
    this.internalMetadata = metadata || {};

    if (logger) {
      this.logger = logger;
    } else {
      this.logger = new DatadogBrowserLogger(prefix, metadata);
    }
  }

  private extractErrorDetails(error: Error) {
    return {
      message: error.message,
      stack: error.stack,
      error,
    };
  }

  private normalizeMetadata(metadata?: LogMetadata): LogMetadata | undefined {
    if (!metadata) {
      return this.internalMetadata;
    }

    if (metadata instanceof Error) {
      return {
        ...this.internalMetadata,
        error: metadata,
        errorDetails: this.extractErrorDetails(metadata),
      };
    }

    if (metadata['error'] && metadata['error'] instanceof Error) {
      return {
        ...this.internalMetadata,
        ...metadata,
        errorDetails: this.extractErrorDetails(metadata['error']),
      };
    }

    return { ...this.internalMetadata, ...metadata };
  }

  info = (message: string, metadata?: LogMetadata) => {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    this.logger.info(message, normalizedMetadata);
  };

  warn = (message: string, metadata?: LogMetadata) => {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    if (this.consoleLogWarningsAsInfo) {
      this.logger.info(message, normalizedMetadata);
    } else {
      this.logger.warn(message, normalizedMetadata);
    }
  };

  error = (message: string, metadata?: LogMetadata) => {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    this.logger.error(message, normalizedMetadata);
  };

  debug = (message: string, metadata?: LogMetadata) => {
    if (isDebugEnabled()) {
      const normalizedMetadata = this.normalizeMetadata(metadata);
      this.logger.debug(message, normalizedMetadata);
    }
  };

  get metadata() {
    return this.internalMetadata;
  }
}

export class RNLogger extends Logger {
  constructor(prefix = 'eluve-logger') {
    super(prefix, { logger: new ConsoleLogger(prefix) });
  }
}

export class NoopLogger extends Logger {
  constructor(prefix = 'eluve-logger') {
    super(prefix, {
      logger: {
        debug: noop,
        error: noop,
        info: noop,
        warn: noop,
      },
    });
  }
}

export type CorrelatedLogger = (
  level: LogLevel,
  message: string,
  metadata?: LogMetadata,
) => void;

export const createCorrelatedLogger = (
  logger: Logger,
  sharedContext: Record<string, string | number | boolean>,
  correlationId = v4(),
) => {
  const log: CorrelatedLogger = (level, message, metadata = {}) => {
    const payload = {
      correlationId,
      ...sharedContext,
    };

    logger[level](message, { ...payload, ...metadata });
  };

  return { correlationId, log };
};
